'use client'

import clsx from 'clsx'
import { formatDate } from 'date-fns/format'
import { NaiveDate } from 'lib/utilities/dateTime'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DayPicker,
  DayPickerBase,
  SelectSingleEventHandler,
} from 'react-day-picker'
import Caption from './partials/Caption'
import { UseDayContentProps, useDayContent } from './partials/DayContent'
import footerWithProps from './partials/Footer'
import HeadRow from './partials/HeadRow'
import Row from './partials/Row'

export interface CalendarWidgetProps {
  /**
   * Optional default date value for the datepicker widget on load. Should be in format `YYYY-MM-DD`
   */
  value?: string
  /**
   * Optional default month to display on the calendar.
   * If not provided, the current month will be used.
   */
  defaultMonth?: Date | string
  /**
   * Optional first eligible date to be selected. Should be in format `YYYY-MM-DD`
   */
  startDate?: string
  /**
   * Optional last eligible date to be selected. Should be in format `YYYY-MM-DD`
   */
  endDate?: string
  /**
   * Optional list of dates to exclude from selection.
   * Can either be a list of string in `YYYY-MM-DD` format,
   * or `react-day-picker`'s `Matcher` type
   */
  excludeDates?: string[] | DayPickerBase['disabled']
  /**
   * Optional custom format for the day content.
   */
  dayContentFormat?: UseDayContentProps['customFormat']
  /**
   * Optional callback function to be called when a date is selected.
   */
  onChange?: (date?: string) => void
  /**
   * Optional class name to apply to the component.
   */
  className?: string
}

export default function CalendarWidget({
  value,
  defaultMonth,
  startDate,
  excludeDates = [],
  endDate,
  className,
  onChange,
  dayContentFormat,
}: CalendarWidgetProps) {
  const [selectedDate, setSelectedDate] = useState<Date>()

  const defaultMonthValue = useMemo(
    () =>
      typeof defaultMonth === 'string'
        ? new NaiveDate(defaultMonth)
        : defaultMonth,
    [defaultMonth],
  )

  const onClearDatesClick = useCallback(() => {
    setSelectedDate(undefined)
    onChange?.(undefined)
  }, [onChange])

  const onSelect = useCallback<SelectSingleEventHandler>(
    (selected?: Date) => {
      setSelectedDate(selected)
      onChange?.(selected ? formatDate(selected, 'yyyy-MM-dd') : undefined)
    },
    [onChange],
  )
  const DayContent = useDayContent({ customFormat: dayContentFormat })
  const Footer = footerWithProps({ onClearDatesClick })

  useEffect(() => {
    if (value) {
      setSelectedDate(new NaiveDate(value))
    }
  }, [value])

  return (
    <DayPicker
      showOutsideDays
      disabled={
        Array.isArray(excludeDates) &&
        excludeDates.some((stringOrDate) => typeof stringOrDate === 'string')
          ? excludeDates.map((stringOrDate) =>
              typeof stringOrDate === 'string'
                ? new Date(stringOrDate)
                : (stringOrDate as Date),
            )
          : (excludeDates as DayPickerBase['disabled'])
      }
      mode="single"
      required
      selected={selectedDate}
      defaultMonth={defaultMonthValue}
      onSelect={onSelect}
      fromDate={startDate ? new Date(startDate) : undefined}
      toDate={endDate ? new Date(endDate) : undefined}
      className={clsx(
        'w-[340px] border border-neutral-400 rounded-8 py-4 px-2 bg-white',
        className,
      )}
      components={{
        Caption,
        Row,
        HeadRow,
        DayContent,
        Footer,
      }}
    />
  )
}
